import { useCallback, useEffect, useRef } from 'react';

import { AUTO_SCROLL_MARGIN_PX } from './treeGeometry';

/** Fastest step per frame, at the very edge of the scroller. */
const MAX_STEP_PX = 18;

export type DragAutoScroll = {
  /** Call on every pointer move with the current client Y. */
  track: (clientY: number) => void;
  /** Call on pointerup/cancel. Safe to call when not scrolling. */
  stop: () => void;
};

/**
 * Vertical auto-scroll while dragging near the top or bottom of the scroller.
 *
 * Driven by requestAnimationFrame rather than by pointer moves: holding the
 * cursor still at the edge produces no further pointermove events, so a
 * move-driven implementation simply stops scrolling — which is the bug the plan
 * tree had. Speed ramps with how far into the margin band the pointer is.
 *
 * `onStep` runs after each scroll so the caller can re-resolve its drop target
 * against rows that just moved under a stationary cursor.
 */
export const useDragAutoScroll = (
  scrollElement: HTMLElement | null,
  onStep?: () => void,
): DragAutoScroll => {
  const frame = useRef<number | null>(null);
  const velocity = useRef(0);
  const onStepRef = useRef(onStep);
  // Kept fresh without re-creating the loop; the rAF closure reads the ref.
  useEffect(() => {
    onStepRef.current = onStep;
  });

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    velocity.current = 0;
  }, []);

  // A drag can end with the component still mounted, but never leave a loop
  // running past unmount.
  useEffect(() => stop, [stop]);

  const track = useCallback(
    (clientY: number) => {
      if (!scrollElement) return;
      const rect = scrollElement.getBoundingClientRect();
      const fromTop = clientY - rect.top;
      const fromBottom = rect.bottom - clientY;

      const ramp = (distance: number) =>
        Math.ceil(MAX_STEP_PX * (1 - Math.max(0, distance) / AUTO_SCROLL_MARGIN_PX));

      if (fromTop < AUTO_SCROLL_MARGIN_PX) {
        velocity.current = -ramp(fromTop);
      } else if (fromBottom < AUTO_SCROLL_MARGIN_PX) {
        velocity.current = ramp(fromBottom);
      } else {
        velocity.current = 0;
      }

      if (velocity.current === 0) {
        stop();
        return;
      }
      if (frame.current !== null) return;

      const step = () => {
        if (velocity.current === 0 || !scrollElement) {
          frame.current = null;
          return;
        }
        const before = scrollElement.scrollTop;
        scrollElement.scrollTop += velocity.current;
        // Hitting either end is not an error — just stop burning frames.
        if (scrollElement.scrollTop === before) {
          frame.current = null;
          return;
        }
        onStepRef.current?.();
        frame.current = requestAnimationFrame(step);
      };
      frame.current = requestAnimationFrame(step);
    },
    [scrollElement, stop],
  );

  return { track, stop };
};
